import { Button, Checkbox, Input, message, Modal, Select } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import React, {
    useEffect,
    useState,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from 'react';
import s from './s.module.less';
import countries from 'constants/country';
import {
    useElements,
    useStripe,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
} from '@stripe/react-stripe-js';
import { emailRegex } from 'pages/SalesProviderExp/constant';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    channelPay,
    getSecretForBankPay,
    submitRecommandChannels,
    updateChannelPay,
    addPaymentInfo,
} from 'api/channel';
import {
    codeOfConductAgreementUrl,
    getPPAChannelId,
    platformAgreementUrl,
} from 'utils/common';
import useChannelDetail from 'hooks/useChannelDetail';
import { PATH } from 'constants/path';
import dayjs from 'utils/dayjs';
import useContractLatestList from 'hooks/useContractLatestList';
import { EBizType } from 'types/common';
// import debounce from 'lodash/debounce';

export interface PaymentContentRef {
    onSubmit: () => Promise<void>;
}

interface IProps {
    title?: string;
    email?: string;
    headerChildren?: React.ReactNode;
    paymentMehodDescription?: string;
    confirmButtonText?: string;
    hidePolicy?: boolean;
    // 隐藏 email + 付款方式表单，只保留 Platform agreement；确认走 onConfirm
    hidePayment?: boolean;
    onConfirm?: () => Promise<void> | void;
    onSuccess?: () => void;
    isPPV?: boolean;
    onPaymentMethodCreated?: (paymentMethodId: string) => void;
    buttonClassName?: string;
}

const filedName = {
    cardNumber: 'cardNumber',
    cardExpiry: 'cardExpiry',
    cardCvc: 'cardCvc',
    email: 'email',
    country: 'country',
    zipCode: 'zipCode',
};

const PaymentContent = forwardRef<PaymentContentRef, IProps>(
    (
        {
            title,
            email,
            headerChildren,
            paymentMehodDescription,
            confirmButtonText,
            hidePolicy = false,
            hidePayment = false,
            onConfirm,
            onSuccess,
            isPPV = false,
            onPaymentMethodCreated,
            buttonClassName,
        }: IProps,
        ref,
    ) => {
        const [channel] = useChannelDetail(Number(getPPAChannelId()), true);
        const [emailValue, setEmailValue] = useState(email);
        const [showErrors, setShowErrors] = useState(false);
        const [selectedPaymentMethod, setSelectedPaymentMethod] =
            useState('card');
        const [country, setCountry] = useState('US');
        const [zipCode, setZipCode] = useState('');
        const [loading, setLoading] = useState(false);
        const [agreementChecked, setAgreementChecked] = useState(false);
        const [agreementChecked2, setAgreementChecked2] = useState(false);
        const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
        const modalContainerRef = React.useRef<HTMLDivElement>(null);
        const [contractLatestList, loadingContractLatestList] = useContractLatestList();

        const [searchParams] = useSearchParams();
        const [agreementType, setAgreementType] = useState<
            'platform' | 'codeOfConduct'
        >('platform');

        const usepPlatformAgreementUrl = contractLatestList.find((item) => item.bizType === EBizType.PPA)?.contractUrl || platformAgreementUrl;
        const useCodeOfConductAgreementUrl = contractLatestList.find((item) => item.bizType === EBizType.KLARITY_CODE_OF_CONDUCT)?.contractUrl || codeOfConductAgreementUrl;
        const type = searchParams.get('type');
        const [fieldsVerifyResult, setFieldsVerifyResult] = useState({
            [filedName.cardNumber]: {
                error: true,
                message: 'Card number is required',
            },
            [filedName.cardExpiry]: {
                error: true,
                message: 'Card expiry date is required',
            },
            [filedName.cardCvc]: {
                error: true,
                message: 'Card CVC is required',
            },
            [filedName.email]: {
                error: false,
                message: 'Please enter the correct email',
            },
            [filedName.zipCode]: {
                error: true,
                message: 'This field is required',
            },
            [filedName.country]: {
                error: false,
                message: 'This field is required',
            },
        });
        const stripe = useStripe();
        const elements = useElements();
        const navigate = useNavigate();
        const bankUsePaymentIntant = true;
        // 银行账户信息状态
        const [bankAccountInfo, setBankAccountInfo] = useState({
            accountHolderName: '',
            routingNumber: '',
            accountNumber: '',
            accountType: 'checking',
            country: 'US',
            zipCode: '',
        });

        useEffect(() => {
            if (email) {
                setEmailValue(email);
            }
        }, [email]);

        const handleEmailChangeDebounce = useCallback((value: string) => {
            setEmailValue(value);
        }, []);

        // 银行账户输入处理函数
        const handleBankInputChange = useCallback(
            (field: string, value: string) => {
                setBankAccountInfo((prev) => ({
                    ...prev,
                    [field]: value,
                }));
            },
            [],
        );

        const handleInputChange = useCallback(
            (e: any, t: string) => {
                if (
                    t === filedName.cardCvc ||
                    t === filedName.cardExpiry ||
                    t === filedName.cardNumber
                ) {
                    // e.complete && e.error (e.error.message)
                    const correct = e.complete && !e.error;
                    setFieldsVerifyResult({
                        ...fieldsVerifyResult,
                        [t]: {
                            error: !correct,
                            message: correct ? '' : e.error?.message,
                        },
                    });
                } else if (t === filedName.country) {
                    setCountry(e);
                    setFieldsVerifyResult({
                        ...fieldsVerifyResult,
                        [t]: {
                            error: !e,
                            message: e ? '' : 'This field is required',
                        },
                    });
                } else if (t === filedName.email) {
                    setEmailValue(e);
                    const emailVerify = emailRegex.test(e);
                    setFieldsVerifyResult({
                        ...fieldsVerifyResult,
                        [t]: {
                            error: !emailVerify,
                            message: emailVerify
                                ? ''
                                : 'Please enter the correct email',
                        },
                    });
                } else {
                    if (t === filedName.zipCode) {
                        setZipCode(e);
                    }
                    setFieldsVerifyResult({
                        ...fieldsVerifyResult,
                        [t]: {
                            error: !e,
                            message: e ? '' : 'This field is required',
                        },
                    });
                }
            },
            [fieldsVerifyResult],
        );

        const handleConfirm = useCallback(async () => {
            // 无付款模式：不创建 stripe payment method，直接走 onConfirm（如 without-payment list）
            if (hidePayment) {
                setLoading(true);
                try {
                    await onConfirm?.();
                } finally {
                    setLoading(false);
                }
                return;
            }

            setShowErrors(true);

            if (!elements || !stripe) {
                return;
            }

            if (selectedPaymentMethod === 'card') {
                if (Object.values(fieldsVerifyResult).every((e) => e.error)) {
                    return;
                }
                const cardElement = elements.getElement(CardNumberElement);
                setLoading(true);
                const res = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement!,
                    billing_details: {
                        email: emailValue || '',
                        address: {
                            country,
                            postal_code: zipCode,
                        },
                    },
                });
                if (!res.error) {
                    const paymentMethodId = res.paymentMethod?.id;

                    // 调用回调函数，将 paymentMethodId 传递给父组件
                    if (paymentMethodId && onPaymentMethodCreated) {
                        onPaymentMethodCreated(paymentMethodId);
                        return;
                    }

                    if (isPPV) {
                        // PPV add payment method
                        const res2 = await addPaymentInfo({
                            paymentMethodId,
                        });
                        if (!res2.error) {
                            onSuccess?.();
                        }
                    } else if (type !== 'update') {
                        // 开通 klarity channel pay
                        const res2 = await channelPay({
                            channelId: channel?.id!,
                            paymentMethodId,
                            email: emailValue || '',
                            country,
                            postalCode: zipCode,
                        });
                        if (!res2.error) {
                            await submitRecommandChannels({
                                channelIdList: [channel?.id!],
                                multServiceList: [],
                            });
                            navigate(`${PATH.CHANNEL_PAYMENT_SUCCESS}`);
                            onSuccess?.();
                        }
                    } else {
                        // update klarity channel pay
                        const res2 = await updateChannelPay({
                            paymentMethodId,
                        });
                        if (!res2.error) {
                            message.success(
                                'Payment method updated successfully. Please note that it may take some time for the changes to take effect.',
                            );
                            navigate(-1);
                            onSuccess?.();
                        }
                    }
                }
                setLoading(false);
            } else if (selectedPaymentMethod === 'bank') {
                try {
                    if (
                        !bankAccountInfo.accountHolderName ||
                        !bankAccountInfo.country ||
                        !bankAccountInfo.zipCode
                    ) {
                        return;
                    }

                    //get clientSecret from backend
                    setLoading(true);
                    const resSecret = await getSecretForBankPay({
                        email: emailValue || '',
                        country: bankAccountInfo.country,
                        postalCode: bankAccountInfo.zipCode,
                    });
                    if (!resSecret.error) {
                        const clientSecret = resSecret.data?.data;
                        const res = await stripe.collectBankAccountForSetup({
                            clientSecret,
                            params: {
                                payment_method_type: 'us_bank_account',
                                payment_method_data: {
                                    billing_details: {
                                        name: bankAccountInfo.accountHolderName,
                                        email: emailValue || '',
                                        // address: {
                                        //     country: bankAccountInfo.country,
                                        //     postal_code: bankAccountInfo.zipCode,
                                        // },
                                    },
                                },
                            },
                        });
                        if (
                            res &&
                            res.setupIntent &&
                            res.setupIntent.payment_method
                        ) {
                            const paymentMethodId = res.setupIntent
                                .payment_method as string;

                            // 必须先 confirm SetupIntent 才会落 ACH mandate，否则后续借记会被 Stripe 以 no_mandate 拒；不能因为 onPaymentMethodCreated 回调就跳过这一步。
                            if (
                                res.setupIntent.status ===
                                'requires_confirmation'
                            ) {
                                const confirmRes =
                                    await stripe.confirmUsBankAccountSetup(
                                        clientSecret,
                                        {
                                            payment_method: paymentMethodId,
                                            mandate_data: {
                                                customer_acceptance: {
                                                    type: 'online',
                                                    online: { infer_from_client: true },
                                                },
                                            },
                                        } as any,
                                    );
                                console.log({
                                    confirmRes,
                                });
                                if (confirmRes.error) {
                                    message.error(
                                        confirmRes.error.message ||
                                        'Bank account confirmation failed',
                                    );
                                    return;
                                }
                                if (
                                    !confirmRes.setupIntent ||
                                    (confirmRes.setupIntent.status !==
                                        'succeeded' &&
                                        confirmRes.setupIntent.status !==
                                        'processing')
                                ) {
                                    message.error(
                                        'Bank account confirmation failed',
                                    );
                                    return;
                                }
                            }

                            if (paymentMethodId && onPaymentMethodCreated) {
                                onPaymentMethodCreated(paymentMethodId);
                                return;
                            }

                            if (isPPV) {
                                const res2 = await addPaymentInfo({
                                    paymentMethodId,
                                });
                                if (!res2.error) {
                                    onSuccess?.();
                                }
                            } else if (type !== 'update') {
                                const res2 = await channelPay({
                                    channelId: channel?.id!,
                                    paymentMethodId: paymentMethodId!,
                                    email: emailValue || '',
                                    country,
                                    postalCode: zipCode,
                                });
                                if (!res2.error) {
                                    await submitRecommandChannels({
                                        channelIdList: [channel?.id!],
                                        multServiceList: [],
                                    });
                                    navigate(`${PATH.CHANNEL_PAYMENT_SUCCESS}`);
                                    onSuccess?.();
                                }
                            } else {
                                const res2 = await updateChannelPay({
                                    paymentMethodId: paymentMethodId!,
                                });
                                if (!res2.error) {
                                    message.success(
                                        'Payment method updated successfully. Please note that it may take some time for the changes to take effect.',
                                    );
                                    navigate(-1);
                                    onSuccess?.();
                                }
                            }
                        }
                    }
                } catch (e) {
                    console.error('Error creating bank PaymentMethod:', e);
                } finally {
                    setLoading(false);
                }
            }
        }, [
            bankAccountInfo,
            channel,
            country,
            elements,
            emailValue,
            fieldsVerifyResult,
            hidePayment,
            onConfirm,
            navigate,
            selectedPaymentMethod,
            stripe,
            type,
            zipCode,
        ]);

        useImperativeHandle(
            ref,
            () => {
                return {
                    onSubmit: () => {
                        return handleConfirm();
                    },
                };
            },
            [handleConfirm],
        );

        return (
            <div className={s.paymentCard}>
                <div
                    ref={modalContainerRef}
                    className={s.fullScreenModalContainer}
                />
                <div className={s.paymentHeader}>
                    {title && <h2 className={s.paymentTitle}>{title}</h2>}
                    {headerChildren && headerChildren}
                </div>

                <div className={s.formSection}>
                    {!hidePayment && (
                    <>
                    {/* Email 输入框 */}
                    <div className={s.formGroup}>
                        <label className={s.label}>Email</label>
                        <Input
                            size="large"
                            className={s.input}
                            value={emailValue}
                            onChange={(e) => {
                                handleEmailChangeDebounce(e.target.value);
                            }}
                            placeholder=""
                        />
                        {showErrors &&
                            fieldsVerifyResult[filedName.email].error && (
                                <div className={s.error}>
                                    {
                                        fieldsVerifyResult[filedName.email]
                                            .message
                                    }
                                </div>
                            )}
                    </div>

                    {/* 支付方式选择 */}
                    <div className={s.formGroup}>
                        <label className={s.label}>Payment method</label>
                        {paymentMehodDescription && (
                            <div className={s.paymentMehodDescription}>
                                {paymentMehodDescription}
                            </div>
                        )}
                        <div className={s.paymentMethods}>
                            <div
                                className={`${s.paymentOption} ${selectedPaymentMethod === 'bank'
                                        ? s.selected
                                        : ''
                                    }`}
                                onClick={() => setSelectedPaymentMethod('bank')}
                            >
                                <div className={s.radioButton}>
                                    {selectedPaymentMethod === 'bank' && (
                                        <div className={s.radioSelected} />
                                    )}
                                </div>
                                <div className={s.paymentIcon}>
                                    <div className={s.bankIcon}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.00031 7.5V14H6.50031V7.5H9.50031V14H11.0003V7.5H14.0003V14H15.0003C15.2655 14 15.5199 14.1054 15.7074 14.2929C15.895 14.4804 16.0003 14.7348 16.0003 15V16H0.000313816V15C0.000313816 14.7348 0.105671 14.4804 0.293207 14.2929C0.480743 14.1054 0.735097 14 1.00031 14H2.00031V7.5H5.00031ZM8.00031 0C12.6813 2.572 15.1813 3.95 15.5003 4.134C15.6925 4.24488 15.8424 4.41655 15.9263 4.62197C16.0103 4.82739 16.0235 5.05489 15.9639 5.26865C15.9044 5.48241 15.7754 5.67028 15.5973 5.80269C15.4192 5.93509 15.2022 6.00451 14.9803 6H1.02031C0.798455 6.00451 0.581399 5.93509 0.403324 5.80269C0.22525 5.67028 0.0962732 5.48241 0.0367046 5.26865C-0.0228641 5.05489 -0.00964091 4.82739 0.0742918 4.62197C0.158224 4.41655 0.308099 4.24488 0.500314 4.134C0.820314 3.95 3.32031 2.572 8.00031 0Z"
                                                fill="#15191E"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <span className={s.paymentLabel}>Bank</span>
                            </div>
                            {selectedPaymentMethod === 'bank' && (
                                <div className={s.bankWrap}>
                                    <div className={s.bankDisclaimer}>
                                        <div className={s.disclaimerIcon}>
                                            ⚠️
                                        </div>
                                        <div className={s.disclaimerText}>
                                            Bank accounts require verification
                                            via microdeposits, which may take
                                            1-2 business days.
                                        </div>
                                    </div>
                                    <div className={s.mandateText}>
                                        By saving your bank account and clicking the confirm button, you authorize Klarity to electronically debit the account you provided for any amount owed for charges arising from your use of Klarity&apos;s services and/or purchase of products from Klarity, pursuant to Klarity&apos;s website and terms, until this authorization is revoked. You may amend or cancel this authorization at any time by providing notice to Klarity with 30 (thirty) days notice.
                                    </div>
                                    <div className={s.formRow}>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Account holder name
                                            </div>
                                            <div className={s.inputBox}>
                                                <Input
                                                    value={
                                                        bankAccountInfo.accountHolderName
                                                    }
                                                    onChange={(e) =>
                                                        handleBankInputChange(
                                                            'accountHolderName',
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Full name on account"
                                                />
                                            </div>
                                            {showErrors &&
                                                !bankAccountInfo.accountHolderName && (
                                                    <div className={s.error}>
                                                        Account holder name is
                                                        required
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                    {!bankUsePaymentIntant && (
                                        <>
                                            <div className={s.formRow}>
                                                <div className={s.formItem}>
                                                    <div className={s.label}>
                                                        Routing number
                                                    </div>
                                                    <div className={s.inputBox}>
                                                        <Input
                                                            value={
                                                                bankAccountInfo.routingNumber
                                                            }
                                                            onChange={(e) =>
                                                                handleBankInputChange(
                                                                    'routingNumber',
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="9-digit routing number"
                                                            maxLength={9}
                                                        />
                                                    </div>
                                                    {showErrors &&
                                                        !bankAccountInfo.routingNumber && (
                                                            <div
                                                                className={
                                                                    s.error
                                                                }
                                                            >
                                                                Routing number
                                                                is required
                                                            </div>
                                                        )}
                                                </div>
                                                <div className={s.formItem}>
                                                    <div className={s.label}>
                                                        Account number
                                                    </div>
                                                    <div className={s.inputBox}>
                                                        <Input
                                                            value={
                                                                bankAccountInfo.accountNumber
                                                            }
                                                            onChange={(e) =>
                                                                handleBankInputChange(
                                                                    'accountNumber',
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            placeholder="Account number"
                                                            maxLength={17}
                                                        />
                                                    </div>
                                                    {showErrors &&
                                                        !bankAccountInfo.accountNumber && (
                                                            <div
                                                                className={
                                                                    s.error
                                                                }
                                                            >
                                                                Account number
                                                                is required
                                                            </div>
                                                        )}
                                                </div>
                                            </div>

                                            <div className={s.formRow}>
                                                <div className={s.formItem}>
                                                    <div className={s.label}>
                                                        Account type
                                                    </div>
                                                    <div className={s.inputBox}>
                                                        <Select
                                                            value={
                                                                bankAccountInfo.accountType
                                                            }
                                                            onChange={(value) =>
                                                                handleBankInputChange(
                                                                    'accountType',
                                                                    value,
                                                                )
                                                            }
                                                            options={[
                                                                {
                                                                    label: 'Checking',
                                                                    value: 'checking',
                                                                },
                                                                {
                                                                    label: 'Savings',
                                                                    value: 'savings',
                                                                },
                                                            ]}
                                                            placeholder="Select account type"
                                                            className={s.select}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className={s.formRow}>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Country
                                            </div>
                                            <div className={s.inputBox}>
                                                <Select
                                                    showSearch
                                                    filterOption={(
                                                        inputValue,
                                                        option,
                                                    ) =>
                                                        !!(
                                                            option &&
                                                            option.label
                                                                .toLowerCase()
                                                                .indexOf(
                                                                    inputValue.toLowerCase(),
                                                                ) >= 0
                                                        )
                                                    }
                                                    value={
                                                        bankAccountInfo.country
                                                    }
                                                    options={countries}
                                                    className={s.select}
                                                    onChange={(e) => {
                                                        handleBankInputChange(
                                                            'country',
                                                            e,
                                                        );
                                                    }}
                                                />
                                            </div>
                                            {showErrors &&
                                                !bankAccountInfo.country && (
                                                    <div className={s.error}>
                                                        Country is required
                                                    </div>
                                                )}
                                        </div>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Postal code
                                            </div>
                                            <div className={s.inputBox}>
                                                <Input
                                                    value={
                                                        bankAccountInfo.zipCode
                                                    }
                                                    type="number"
                                                    maxLength={8}
                                                    onChange={(e) => {
                                                        handleBankInputChange(
                                                            'zipCode',
                                                            e.target.value,
                                                        );
                                                    }}
                                                />
                                            </div>
                                            {showErrors &&
                                                !bankAccountInfo.zipCode && (
                                                    <div className={s.error}>
                                                        Postal code is required
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div
                                className={`${s.paymentOption} ${selectedPaymentMethod === 'card'
                                        ? s.selected
                                        : ''
                                    }`}
                                onClick={() => setSelectedPaymentMethod('card')}
                            >
                                <div className={s.radioButton}>
                                    {selectedPaymentMethod === 'card' && (
                                        <div className={s.radioSelected} />
                                    )}
                                </div>
                                <div className={s.paymentIcon}>
                                    <div className={s.cardIcon}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="12"
                                            viewBox="0 0 16 12"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M0 2C0 1.46957 0.210714 0.960859 0.585786 0.585786C0.960859 0.210714 1.46957 0 2 0H14C14.5304 0 15.0391 0.210714 15.4142 0.585786C15.7893 0.960859 16 1.46957 16 2H0ZM0 4V10C0 10.5304 0.210714 11.0391 0.585786 11.4142C0.960859 11.7893 1.46957 12 2 12H14C14.5304 12 15.0391 11.7893 15.4142 11.4142C15.7893 11.0391 16 10.5304 16 10V4H0ZM3 9C3 8.73478 3.10536 8.48043 3.29289 8.29289C3.48043 8.10536 3.73478 8 4 8H5C5.26522 8 5.51957 8.10536 5.70711 8.29289C5.89464 8.48043 6 8.73478 6 9C6 9.26522 5.89464 9.51957 5.70711 9.70711C5.51957 9.89464 5.26522 10 5 10H4C3.73478 10 3.48043 9.89464 3.29289 9.70711C3.10536 9.51957 3 9.26522 3 9Z"
                                                fill="#15191E"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <span className={s.paymentLabel}>Card</span>
                                <div className={s.cardBrands}>
                                    {selectedPaymentMethod !== 'card' && (
                                        <div className={s.cardIconList}>
                                            <div
                                                className={`${s.card} ${s.mastercard}`}
                                            />
                                            <div
                                                className={`${s.card} ${s.amex}`}
                                            />
                                            <div
                                                className={`${s.card} ${s.discover}`}
                                            />
                                            <div
                                                className={`${s.card} ${s.visa}`}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {selectedPaymentMethod === 'card' && (
                                <div className={s.cardWrap}>
                                    <div className={s.cardTips}>
                                        A 3% processing fee applies when paying
                                        by credit card.
                                    </div>
                                    <div className={s.formRow}>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Card Number
                                            </div>
                                            <div className={s.inputBox}>
                                                <div
                                                    className={s.inputBoxInner}
                                                >
                                                    <CardNumberElement
                                                        className={`${s.input} ${s.inputWithoutBorder}`}
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                e,
                                                                filedName.cardNumber,
                                                            )
                                                        }
                                                    />
                                                    <div
                                                        className={
                                                            s.cardIconList
                                                        }
                                                    >
                                                        <div
                                                            className={`${s.card} ${s.mastercard}`}
                                                        />
                                                        <div
                                                            className={`${s.card} ${s.amex}`}
                                                        />
                                                        <div
                                                            className={`${s.card} ${s.discover}`}
                                                        />
                                                        <div
                                                            className={`${s.card} ${s.visa}`}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {showErrors &&
                                                fieldsVerifyResult[
                                                    filedName.cardNumber
                                                ].error && (
                                                    <div className={s.error}>
                                                        {
                                                            fieldsVerifyResult[
                                                                filedName
                                                                    .cardNumber
                                                            ].message
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                    <div className={s.formRow}>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Expiration
                                            </div>
                                            <div className={s.inputBox}>
                                                <CardExpiryElement
                                                    className={s.input}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            e,
                                                            filedName.cardExpiry,
                                                        )
                                                    }
                                                />
                                            </div>
                                            {showErrors &&
                                                fieldsVerifyResult[
                                                    filedName.cardExpiry
                                                ].error && (
                                                    <div className={s.error}>
                                                        {
                                                            fieldsVerifyResult[
                                                                filedName
                                                                    .cardExpiry
                                                            ].message
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                        <div className={s.formItem}>
                                            <div className={s.label}>CVC</div>
                                            <div className={s.inputBox}>
                                                <CardCvcElement
                                                    className={s.input}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            e,
                                                            filedName.cardCvc,
                                                        )
                                                    }
                                                />
                                            </div>
                                            {showErrors &&
                                                fieldsVerifyResult[
                                                    filedName.cardCvc
                                                ].error && (
                                                    <div className={s.error}>
                                                        {
                                                            fieldsVerifyResult[
                                                                filedName
                                                                    .cardCvc
                                                            ].message
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                    <div className={s.formRow}>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Country
                                            </div>
                                            <div className={s.inputBox}>
                                                <Select
                                                    showSearch
                                                    filterOption={(
                                                        inputValue,
                                                        option,
                                                    ) =>
                                                        !!(
                                                            option &&
                                                            option.label
                                                                .toLowerCase()
                                                                .indexOf(
                                                                    inputValue.toLowerCase(),
                                                                ) >= 0
                                                        )
                                                    }
                                                    value={country}
                                                    options={countries}
                                                    className={s.select}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            e,
                                                            filedName.country,
                                                        )
                                                    }
                                                />
                                            </div>
                                            {showErrors &&
                                                fieldsVerifyResult[
                                                    filedName.country
                                                ].error && (
                                                    <div className={s.error}>
                                                        {
                                                            fieldsVerifyResult[
                                                                filedName
                                                                    .country
                                                            ].message
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                        <div className={s.formItem}>
                                            <div className={s.label}>
                                                Postal code
                                            </div>
                                            <div className={s.inputBox}>
                                                <Input
                                                    value={zipCode}
                                                    type="number"
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            e.target.value,
                                                            filedName.zipCode,
                                                        )
                                                    }
                                                />
                                            </div>
                                            {showErrors &&
                                                fieldsVerifyResult[
                                                    filedName.zipCode
                                                ].error && (
                                                    <div className={s.error}>
                                                        {
                                                            fieldsVerifyResult[
                                                                filedName
                                                                    .zipCode
                                                            ].message
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    </>
                    )}
                    {!hidePolicy && (
                        <>
                            <div className={s.premiumChannel}>
                                <div className={s.title}>
                                    Platform agreement confirmation
                                </div>
                                <div className={s.desc}>
                                    To ensure transparency and a smooth
                                    experience, please review and agree to our
                                    Platform Agreement and Code of Conduct
                                    before moving forward.
                                </div>
                                <div className={s.agreeNav}>
                                    <div
                                        className={`${s.agreeNavItem} ${agreementType === 'platform'
                                                ? s.active
                                                : ''
                                            }`}
                                        onClick={() =>
                                            setAgreementType('platform')
                                        }
                                    >
                                        Platform Agreement
                                    </div>
                                    <div
                                        className={`${s.agreeNavItem} ${agreementType === 'codeOfConduct'
                                                ? s.active
                                                : ''
                                            }`}
                                        onClick={() =>
                                            setAgreementType('codeOfConduct')
                                        }
                                    >
                                        Code of Conduct
                                    </div>
                                </div>
                                {agreementType === 'platform' && (
                                    <div className={s.iframeContainer}>
                                        <iframe
                                            className={s.iframe}
                                            title="overview"
                                            src={usepPlatformAgreementUrl}
                                        />
                                        <Button
                                            className={s.expandButton}
                                            icon={<ExpandOutlined />}
                                            onClick={() =>
                                                setIsFullScreenOpen(true)
                                            }
                                            type="text"
                                            title="Expand to full screen"
                                        />
                                    </div>
                                )}
                                {agreementType === 'codeOfConduct' && (
                                    <div className={s.iframeContainer}>
                                        <iframe
                                            className={s.iframe}
                                            title="overview"
                                            src={useCodeOfConductAgreementUrl}
                                        />
                                        <Button
                                            className={s.expandButton}
                                            icon={<ExpandOutlined />}
                                            onClick={() =>
                                                setIsFullScreenOpen(true)
                                            }
                                            type="text"
                                            title="Expand to full screen"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className={s.check}>
                                <div>
                                    <Checkbox
                                        checked={agreementChecked}
                                        onChange={(e) =>
                                            setAgreementChecked(
                                                e.target.checked,
                                            )
                                        }
                                    >
                                        <span className={s.checkText}>
                                            I agree to the terms outlined in the
                                            agreement on{' '}
                                            {dayjs().format('MM/DD/YYYY')}.
                                        </span>
                                    </Checkbox>
                                </div>
                                <div>
                                    <Checkbox
                                        checked={agreementChecked2}
                                        onChange={(e) =>
                                            setAgreementChecked2(
                                                e.target.checked,
                                            )
                                        }
                                    >
                                        <span className={s.checkText}>
                                            I agree to follow the Klarity
                                            Provider Code of Conduct.
                                        </span>
                                    </Checkbox>
                                </div>
                            </div>
                        </>
                    )}

                    {/* 全屏查看模态框 */}
                    {!hidePolicy && (
                        <Modal
                            title={
                                agreementType === 'platform'
                                    ? 'Platform Agreement'
                                    : 'Code of Conduct Agreement'
                            }
                            open={isFullScreenOpen}
                            onCancel={() => setIsFullScreenOpen(false)}
                            footer={null}
                            width="90vw"
                            style={{ top: 20 }}
                            bodyStyle={{ height: '85vh', padding: 0 }}
                            zIndex={10000}
                            wrapClassName={s.fullScreenModalWrap}
                            getContainer={() =>
                                modalContainerRef.current || document.body
                            }
                        >
                            <iframe
                                className={s.fullScreenIframe}
                                title="overview-fullscreen"
                                src={
                                    agreementType === 'platform'
                                        ? usepPlatformAgreementUrl
                                        : useCodeOfConductAgreementUrl
                                }
                            />
                        </Modal>
                    )}

                    {/* 确认按钮 */}
                    {confirmButtonText && (
                        <div className={s.buttonSection}>
                            <Button
                                className={`${s.confirmButton} ${buttonClassName}`}
                                onClick={handleConfirm}
                                size="large"
                                type="primary"
                                loading={loading}
                                disabled={
                                    !hidePolicy &&
                                    (!agreementChecked || !agreementChecked2)
                                }
                            >
                                {confirmButtonText}
                            </Button>

                            {!hidePayment && (
                            <div className={s.securityInfo}>
                                By confirming, you authorize the payment using
                                your saved info via Stripe, ensuring a secure
                                transaction.
                            </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    },
);

export default PaymentContent;
